import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { BlogPost } from './ResourceContent';

type Props = {
  post: BlogPost;
  typeColors: Record<string, string>;
  expanded: boolean;
  onExpand: () => void;
};

const categoryColors: Record<string, string> = {
  'Immigration Law': 'bg-rose-100 text-rose-700',
  'Civic Rights': 'bg-blue-100 text-blue-700',
  'Advocacy Guides': 'bg-amber-100 text-amber-700',
  'Legal Aid': 'bg-emerald-100 text-emerald-700',
};

export default function BlogPostCard({ post, typeColors, expanded, onExpand }: Props) {
  return (
    <div className="card-base card-hover flex flex-col p-5">
      {/* Student disclaimer */}
      <div className="flex items-center gap-1.5 mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
        <Icon name="AcademicCapIcon" size={13} variant="solid" className="text-amber-600 flex-shrink-0" />
        <span className="text-xs text-amber-800 font-medium">
          Student-written · {post.school}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className={`badge-skill ${typeColors[post.type]}`}>{post.type}</span>
        <span className={`badge-skill ${categoryColors[post.category]}`}>{post.category}</span>
      </div>
      <h3 className="text-base font-bold text-foreground mb-2 leading-snug line-clamp-2">
        {post.title}
      </h3>
      <p className="text-sm text-zinc-500 leading-relaxed mb-4 flex-1 line-clamp-3">
        {post.excerpt}
      </p>

      {/* Full article content */}
      {expanded && (
        <div className="mb-4 p-4 rounded-xl bg-zinc-50 border border-border">
          <div className="prose prose-sm max-w-none text-zinc-700 leading-relaxed whitespace-pre-line text-sm">
            {post.fullContent}
          </div>
        </div>
      )}

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {post.tags.slice(0, 2).map((tag) => (
          <span
            key={`tag-${post.id}-${tag}`}
            className="px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-600 text-xs font-medium"
          >
            #{tag}
          </span>
        ))}
      </div>

      {/* Author + meta */}
      <div className="flex items-center gap-3 pt-4 border-t border-border">
        <div className="w-8 h-8 rounded-full crimson-gradient flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xs font-bold">{post.author[0]}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground truncate">{post.author}</p>
          <p className="text-xs text-muted-foreground truncate">{post.school}</p>
        </div>
        <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground flex-shrink-0">
          <span>{post.date}</span>
          <span className="flex items-center gap-1">
            <Icon name="ClockIcon" size={11} variant="outline" />
            {post.readTime}
          </span>
        </div>
      </div>

      <button
        onClick={onExpand}
        className="btn-ghost w-full mt-3 text-primary hover:text-primary hover:bg-primary/5 justify-center text-xs font-semibold"
        suppressHydrationWarning
      >
        {expanded ? 'Collapse Article' : 'Read Article'}
        <Icon name={expanded ? 'ChevronUpIcon' : 'ArrowRightIcon'} size={13} variant="outline" />
      </button>
    </div>
  );
}